/**
 * Argus Tool / Function Developer — Welcome + File-upload helpers.
 *
 * History:
 *   - Round 1: this file shipped a mock "conversation engine" that
 *     keyword-matched user input and returned a hardcoded
 *     `custom.equipment_status` plant-monitoring tool every time
 *     (bug #95: the wizard ignored the user's actual request, e.g.
 *     "make me an uppercase function with no external access").
 *   - Day 5 §6.4 (PR-AA1): `ToolDeveloperPage.tsx` was wired to the
 *     real Function Helper / Tool Helper LLM via `useHelperChat`. The
 *     mock `generateMockResponse` path stopped being called.
 *   - 2026-05-22 (#95 closure): the dead mock branches + the
 *     hardcoded `custom.equipment_status` `MOCK_TS_CODE` /
 *     `MOCK_TS_IMPROVED` / `MOCK_PY_CODE` templates were deleted so
 *     no future regression can re-import them and resurrect the
 *     "agent ignores user, returns plant API tool" failure mode.
 *
 * What's left here is intentionally tiny:
 *   - `getWelcomeMessages(entityKind)` — the first-message shell the
 *     real-LLM chat appends its turns onto. Tool vs. Function copy.
 *   - `handleFileUpload(name, content)` — the static acknowledgement
 *     message shown after a user drops a reference file into the
 *     chat. The real LLM picks the attached content up via the
 *     `useHelperChat` payload; this helper only renders the local
 *     "I see your file" bubble.
 */

import type { ToolDevMessage } from './types';

// ── Welcome Messages ───────────────────────────────────────────────────────

/**
 * R2 P1-3 (PR #470 audit fix): take an optional `entityKind` so the
 * shared shell can render the right "I'm <X>" intro for either the
 * tools authoring flow or the functions authoring flow. The chips
 * also retarget — "build a function" vs "build a tool" framing.
 *
 * Defaults to 'tool' for back-compat with the existing tools route.
 */
export function getWelcomeMessages(
  entityKind: 'tool' | 'function' = 'tool',
): Omit<ToolDevMessage, 'id' | 'timestamp'>[] {
  if (entityKind === 'function') {
    return [
      {
        role: 'argus',
        type: 'text',
        content: `Hey! I'm **Argus Function Developer** — I'll help you write custom functions that run as part of your workflows and agents.\n\nFunctions are small sandboxed code units (TypeScript or Python) that transform data, call external APIs, or compute derived values. Tell me what you need your function to do and I'll draft the code, review it for security, and help you test it.\n\n**What should the function do?**`,
      },
      {
        role: 'argus',
        type: 'suggestion_chips',
        content: '',
        chips: [
          { id: 'transform', label: 'Transform data', emoji: '🧮', action: 'I need a function that transforms or reshapes data passed in by an agent or workflow' },
          { id: 'api', label: 'Call an external API', emoji: '🌐', action: 'I need a function that calls an external REST API and returns the response' },
          { id: 'validate', label: 'Validate / parse input', emoji: '✅', action: 'I need a function that validates or parses an incoming payload before it reaches a workflow step' },
          { id: 'custom', label: 'Something else', emoji: '✨', action: 'I have a specific function use case I want to describe' },
        ],
      },
    ];
  }
  return [
    {
      role: 'argus',
      type: 'text',
      content: `Hey! I'm **Argus Tool Developer** — I'll help you build custom tools that your agents can use.\n\nTell me what you need your tool to do, and I'll write the code, review it for security, and help you test it.\n\n**What kind of tool do you want to build?**`,
    },
    {
      role: 'argus',
      type: 'suggestion_chips',
      content: '',
      chips: [
        { id: 'api', label: 'Connect to an API', emoji: '🌐', action: 'I need a tool that calls an external REST API to fetch data' },
        { id: 'data', label: 'Process data', emoji: '📊', action: 'I need a tool that transforms or analyzes data from our systems' },
        { id: 'notify', label: 'Send notifications', emoji: '📨', action: 'I need a tool that sends notifications to Slack, Teams, or email' },
        { id: 'custom', label: 'Something else', emoji: '✨', action: 'I have a specific use case I want to describe' },
      ],
    },
  ];
}

// ── File Upload Handler ────────────────────────────────────────────────────

/**
 * Returns the in-chat "I've received your file" acknowledgement
 * bubble after the user drops a reference doc / code snippet / API
 * spec. The actual file content travels to the LLM via the
 * `useHelperChat` payload — this helper only renders the local
 * "I see what you uploaded" message.
 */
export function handleFileUpload(fileName: string, content: string): Omit<ToolDevMessage, 'id' | 'timestamp'>[] {
  const ext = fileName.split('.').pop()?.toLowerCase();

  if (ext === 'md' || ext === 'txt') {
    return [{
      role: 'argus',
      type: 'text',
      content: `I've read **${fileName}**. I can see this is documentation for an API/system. Let me use this as a reference to build your tool.\n\nBased on this document, I can:\n- Extract the API endpoints and authentication method\n- Understand the data model and response format\n- Generate parameter schemas that match the API\n\nShall I generate the tool code based on this documentation?`,
    }];
  }

  if (ext === 'ts' || ext === 'js' || ext === 'py') {
    return [{
      role: 'argus',
      type: 'text',
      content: `I've reviewed the code in **${fileName}** (${content.split('\n').length} lines). I can:\n\n- Use this as a **template** and adapt it for your tool\n- **Analyze** the patterns and suggest improvements\n- **Extract** the API calls and convert them to our tool format\n\nWhat would you like me to do with this code?`,
    }];
  }

  if (ext === 'json' || ext === 'yaml' || ext === 'yml') {
    return [{
      role: 'argus',
      type: 'text',
      content: `I've parsed **${fileName}**. This looks like an API specification. I'll use it to:\n\n- Auto-generate parameter schemas\n- Map endpoint paths to tool functions\n- Configure the correct authentication method\n\nGenerating the tool code now...`,
    }];
  }

  return [{
    role: 'argus',
    type: 'text',
    content: `I've received **${fileName}**. I'll use this as reference material when building your tool.`,
  }];
}
